/**
 * Detective Mentor System
 * Manages the visual detective character that guides the user
 */

const DetectiveMentor = {
    // Configuration
    config: {
        defaultCharacter: 'boy', // 'boy' or 'girl'
        currentCharacter: null,
        currentState: 'neutral',
        transitionDuration: 300 // ms
    },

    // Character states
    states: {
        neutral: 'neutral',
        happy: 'happy',
        thinking: 'thinking',
        encouraging: 'encouraging'
    },

    // DOM element
    avatarElement: null,
    imageElement: null,
    accessoryElements: {}, // Map of slot -> element

    /**
     * Initialize the mentor system
     */
    init() {
        // Cargar preferencia del personaje mentor
        const savedChar = window.UserManager ? window.UserManager.loadData('mentorCharacter') : null;
        this.config.currentCharacter = savedChar || this.config.defaultCharacter || 'boy';

        // Crear elemento si no existe
        this.createAvatarElement();

        // Estado inicial
        this.setState('neutral');

        // Load backpack items
        setTimeout(() => this.loadBackpackItems(), 500);

        console.log(`🕵️ Detective Mentor inicializado: ${this.config.currentCharacter}`);
    },

    /**
     * Create the avatar DOM element
     */
    createAvatarElement() {
        // Check if already exists
        this.avatarElement = document.getElementById('mentor-avatar');

        if (!this.avatarElement) {
            // Create container
            this.avatarElement = document.createElement('div');
            this.avatarElement.id = 'mentor-avatar';
            this.avatarElement.className = 'mentor-avatar';
            this.avatarElement.style.cursor = 'pointer'; // Indicador de click
            this.avatarElement.onclick = () => {
                // Si hay un texto guardado, lo repetimos y animamos el avatar
                if (window.lastSpokenText && window.speak) {
                    this.animate('talk');
                    window.speak(window.lastSpokenText, window.currentLanguage);
                }
            };

            // Create image element
            this.imageElement = document.createElement('img'); this.imageElement.onerror = () => { this.imageElement.style.display = 'none'; };
            this.imageElement.alt = 'Detective Mentor';
            this.imageElement.className = 'mentor-image';
            this.avatarElement.appendChild(this.imageElement);

            document.body.appendChild(this.avatarElement);

            // Restore accessories if saved
            const restoreAccessories = (attempts = 0) => {
                if (attempts > 20) return; // Prevent infinite loop
                if (!window.StickerStore) {
                    setTimeout(() => restoreAccessories(attempts + 1), 100);
                    return;
                }
                const savedAccs = window.UserManager ? window.UserManager.loadData('mentorAccessories') : {};
                if (savedAccs && Object.keys(savedAccs).length > 0) {
                    Object.values(savedAccs).forEach(itemId => {
                        const item = window.StickerStore.catalog.find(i => i.id === itemId);
                        if (item) this.setAccessory(item);
                    });
                } else {
                    // Compatibility for transition from single to multiple
                    const savedAccId = window.UserManager ? window.UserManager.loadData('mentorAccessoryId') : null;
                    if (savedAccId) {
                        const item = window.StickerStore.catalog.find(i => i.id === savedAccId);
                        if (item) this.setAccessory(item);
                    }
                }
            };
            restoreAccessories();

        } else {
            this.imageElement = this.avatarElement.querySelector('img');
        }
    },

    /**
     * Set the accessory (item object) supporting multiple slots
     */
    setAccessory(item) {
        if (!this.avatarElement) return;

        if (!item) return; // For clearing, we'd need a clear method or specific logic

        // Determine Slot
        let slot = 'misc';
        if (item.id.includes('hat') || item.id.includes('crown') || item.id.includes('wizard')) slot = 'head';
        else if (item.id.includes('glasses')) slot = 'eyes';
        else if (item.id.includes('cape')) slot = 'back'; // Cape goes on back
        else if (item.id.includes('magnifying') || item.id.includes('toy') || item.id.includes('viking')) slot = 'hand';
        else if (item.id.includes('pet')) slot = 'pet';
        else if (item.id.includes('hero')) slot = 'back';
        else if (item.category === 'bg') slot = 'bg';

        // 1. Get or Create element for THIS slot
        let element = this.accessoryElements[slot];
        if (!element) {
            element = document.createElement('div');
            element.className = 'mentor-accessory';
            this.avatarElement.appendChild(element);
            this.accessoryElements[slot] = element;
        }

        // 2. Clear old item classes from this slot
        element.className = 'mentor-accessory';

        // 3. Add specific positioning classes
        if (item.id.includes('hat')) element.classList.add('acc-on-head');
        else if (item.id.includes('glasses')) element.classList.add('acc-on-eyes');
        else if (item.id.includes('cape')) element.classList.add('acc-on-back'); // Cape positioning
        else if (item.id.includes('crown')) element.classList.add('acc-on-head-high');
        else if (item.id.includes('wizard')) element.classList.add('acc-on-head-wizard');
        else if (item.id.includes('magnifying') || item.id.includes('viking')) element.classList.add('acc-in-hand');
        else if (item.id.includes('pet')) element.classList.add('acc-side-pet');
        else if (item.category === 'bg') element.classList.add('acc-background');

        // Character specific tweaks
        element.classList.add(`char-${this.config.currentCharacter}`);

        element.textContent = item.icon;
        element.classList.remove('hidden');

        // 4. Save state (Multiple)
        if (window.UserManager) {
            const savedAccs = window.UserManager.loadData('mentorAccessories') || {};
            savedAccs[slot] = item.id;
            window.UserManager.saveData('mentorAccessories', savedAccs);

            // Legacy support
            window.UserManager.saveData('mentorAccessoryId', item.id);
        }

        // Pulse effect
        element.classList.remove('pop-in');
        void element.offsetWidth; // force reflow
        element.classList.add('pop-in');
    },

    /**
     * Add item to backpack (non-wearable items that appear around detective)
     * @param {object} item - The item object from catalog
     */
    addBackpackItem(item) {
        if (!this.avatarElement) return;
        if (!item) return;

        // Initialize backpack items array if not exists
        if (!this.backpackItems) {
            this.backpackItems = [];
        }

        // Check if item already in backpack
        const existingIndex = this.backpackItems.findIndex(i => i.id === item.id);
        if (existingIndex !== -1) {
            // Item already in backpack, just show it
            this.renderBackpackItems();
            return;
        }

        // Add to backpack
        this.backpackItems.push(item);

        // Save to user data
        if (window.UserManager) {
            const backpackIds = this.backpackItems.map(i => i.id);
            window.UserManager.saveData('mentorBackpack', backpackIds);
        }

        // Render all backpack items
        this.renderBackpackItems();
    },

    /**
     * Render all backpack items around the detective
     */
    renderBackpackItems() {
        if (!this.avatarElement) return;

        // Remove existing backpack items
        const existingItems = this.avatarElement.querySelectorAll('.mentor-backpack-item');
        existingItems.forEach(el => el.remove());

        if (!this.backpackItems || this.backpackItems.length === 0) return;

        // Position items in a circular pattern around the detective
        const positions = [
            { class: 'bp-top-left', label: 'Arriba Izquierda' },
            { class: 'bp-top-right', label: 'Arriba Derecha' },
            { class: 'bp-left', label: 'Izquierda' },
            { class: 'bp-right', label: 'Derecha' },
            { class: 'bp-bottom-left', label: 'Abajo Izquierda' },
            { class: 'bp-bottom-right', label: 'Abajo Derecha' },
            { class: 'bp-bottom', label: 'Abajo' },
            { class: 'bp-top', label: 'Arriba' }
        ];

        this.backpackItems.forEach((item, index) => {
            const element = document.createElement('div');
            element.className = 'mentor-backpack-item';

            // Use position from array, cycle if more items than positions
            const position = positions[index % positions.length];
            element.classList.add(position.class);

            // Set icon
            element.textContent = item.icon;
            element.title = item.name;

            // Add animation
            element.style.animationDelay = `${index * 0.1}s`;

            this.avatarElement.appendChild(element);
        });
    },

    /**
     * Load backpack items from saved data
     */
    loadBackpackItems() {
        if (!window.UserManager || !window.StickerStore) return;

        const backpackIds = window.UserManager.loadData('mentorBackpack');
        if (!backpackIds || backpackIds.length === 0) return;

        this.backpackItems = [];
        backpackIds.forEach(itemId => {
            const item = window.StickerStore.catalog.find(i => i.id === itemId);
            if (item) {
                this.backpackItems.push(item);
            }
        });

        this.renderBackpackItems();
    },


    /**
     * Set the mentor's state
     * @param {string} state - One of: neutral, happy, thinking, encouraging
     */
    setState(state) {
        if (!this.states[state]) {
            console.warn(`⚠️ Invalid mentor state: ${state}`);
            return;
        }

        this.config.currentState = state;
        this.updateImage();
    },

    /**
     * Update the displayed image
     */
    updateImage() {
        const char = this.config.currentCharacter;
        const state = this.config.currentState;
        let imagePath = `images/detectives/${char}/detective_${char}_${state}.png`;

        // ED-03: Usar avatar personalizado de la editorial si está configurado
        if (window.PUBLISHER_CONFIG && window.PUBLISHER_CONFIG.branding && window.PUBLISHER_CONFIG.branding.detectiveAvatarUrl) {
            imagePath = window.PUBLISHER_CONFIG.branding.detectiveAvatarUrl;
        }

        if (this.imageElement) {
            // Add fade transition
            this.imageElement.style.opacity = '0';

            setTimeout(() => {
                this.imageElement.src = imagePath;
                this.imageElement.style.opacity = '1';
            }, this.config.transitionDuration / 2);
        }
    },

    /**
     * Set the mentor's character
     * @param {string} character - 'boy' or 'girl'
     */
    setCharacter(character) {
        if (character !== 'boy' && character !== 'girl') {
            console.warn(`⚠️ Invalid character: ${character}`);
            return;
        }

        const oldChar = this.config.currentCharacter;
        this.config.currentCharacter = character;
        if (window.UserManager) {
            window.UserManager.saveData('mentorCharacter', character);
        }

        // If character actually changed, update the image
        if (oldChar !== character) {
            this.updateImage();
        }

        console.log(`🕵️ Character set to ${character}`);
    },

    /**
     * Select a random character
     */
    randomizeCharacter() {
        const characters = ['boy', 'girl'];
        const randomChar = characters[Math.floor(Math.random() * characters.length)];
        this.setCharacter(randomChar);
    },

    /**
     * Switch to the other character
     */
    alternateCharacter() {
        const next = this.config.currentCharacter === 'boy' ? 'girl' : 'boy';
        this.setCharacter(next);
    },

    /**
     * Show the mentor
     */
    show() {
        if (!this.avatarElement) {
            this.init();
        }
        if (this.avatarElement) {
            this.avatarElement.classList.add('visible');
        }
    },

    /**
     * Hide the mentor
     */
    hide() {
        if (window.stopAudio) window.stopAudio();
        if (this.avatarElement) {
            this.avatarElement.classList.remove('visible');
        }
    },

    /**
     * React to user events
     */
    onCorrectAnswer() {
        this.setState('happy');
        setTimeout(() => this.setState('neutral'), 2000);
    },

    onWrongAnswer() {
        this.setState('encouraging');
        setTimeout(() => this.setState('neutral'), 3000);
    },

    onReading() {
        this.setState('thinking');
    },

    onIdle() {
        this.setState('neutral');
    },

    /**
     * Make the avatar jump for joy using GSAP Physics (Squash & Stretch)
     */
    jump() {
        if (!this.avatarElement) return;

        // Remove CSS animation class if present to let GSAP take over
        this.avatarElement.classList.remove('jump-animation');

        // Set happy state
        this.setState('happy');

        // Check if GSAP is loaded
        if (window.gsap) {
            const tl = gsap.timeline({
                onComplete: () => this.setState('neutral')
            });

            // Physics-based Squash & Stretch Sequence
            tl.to(this.avatarElement, {
                scaleY: 0.7, // Squash down
                scaleX: 1.2,
                duration: 0.15,
                ease: "power2.in"
            })
                .to(this.avatarElement, {
                    y: -150, // Jump High
                    scaleY: 1.1, // Stretch up
                    scaleX: 0.9,
                    duration: 0.35,
                    ease: "power2.out"
                })
                .to(this.avatarElement, {
                    y: 0, // Land
                    scaleY: 0.6, // Squash on impact
                    scaleX: 1.3,
                    duration: 0.4,
                    ease: "bounce.out" // Physical bounce effect
                })
                .to(this.avatarElement, {
                    scaleX: 1,
                    scaleY: 1,
                    duration: 0.2,
                    ease: "power2.out"
                });

        } else {
            // Fallback for when GSAP isn't loaded (e.g. offline)
            this.avatarElement.classList.add('jump-animation');
            setTimeout(() => this.setState('neutral'), 1500);
        }
    },

    /**
     * Speak a message via speech bubble
     * @param {string} message - The text to say
     * @param {string} type - 'neutral', 'success', 'error'
     * @param {number} duration - If > 0, overrides auto-calculation for auto-close
     * @param {function} onDismiss - Optional callback when bubble is dismissed
     * @param {boolean} suppressAudio - If true, audio playback will be skipped
     */
    speak(message, type = 'neutral', duration = 0, onDismiss = null, suppressAudio = false) {
        if (!message) return;
        if (!this.avatarElement) this.createAvatarElement();

        // Reset any existing timeout
        if (this.speechTimeout) {
            clearTimeout(this.speechTimeout);
            this.speechTimeout = null;
        }

        // Set callback
        this.onBubbleClose = onDismiss;

        // 1. Find or create bubble
        let bubble = this.avatarElement.querySelector('.mentor-speech-bubble');
        if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'mentor-speech-bubble';
            this.avatarElement.appendChild(bubble);
        }

        // 2. Set content
        bubble.innerHTML = '';

        // Add Close Button ONLY for non-success messages
        if (type !== 'success') {
            const closeBtn = document.createElement('button');
            closeBtn.className = 'mentor-bubble-close';
            closeBtn.innerHTML = '✕';
            closeBtn.onclick = (e) => {
                e.stopPropagation();
                this.hideBubble();
            };
            bubble.appendChild(closeBtn);
        }

        // Add Audio Button
        const audioBtn = document.createElement('button');
        audioBtn.className = 'mentor-bubble-audio';
        audioBtn.innerHTML = '🔊';
        audioBtn.onclick = (e) => {
            e.stopPropagation();
            if (window.toggleAudio) {
                window.toggleAudio(audioBtn, message);
            } else {
                if (window.AudioService) window.AudioService.stop();
                if (window.AudioService) window.AudioService.speak(message);
                else if (window.speak) window.speak(message);
            }
        };
        bubble.appendChild(audioBtn);

        const textSpan = document.createElement('span');
        textSpan.textContent = message;
        bubble.appendChild(textSpan);

        // 3. Set Styling & State
        bubble.className = 'mentor-speech-bubble visible';
        if (type === 'success') {
            bubble.classList.add('success');
            this.setState('happy');
        } else if (type === 'error') {
            bubble.classList.add('error');
            this.setState('encouraging');
        } else {
            this.setState('neutral');
        }

        // 4. Play audio unless suppressed
        if (!suppressAudio) {
            if (window.toggleAudio && audioBtn) {
                window.toggleAudio(audioBtn, message);
            } else if (window.AudioService) {
                window.AudioService.speak(message);
            } else if (window.speak) {
                window.speak(message);
            }
        }

        // 5. Auto-Closing Logic
        if (type === 'success' || duration > 0) {
            const estimatedTime = duration > 0 ? duration : (message.length * 70) + 2000;
            this.speechTimeout = setTimeout(() => this.hideBubble(), estimatedTime);
        }
    },

    /**
     * Say text and automatically hide bubble when audio ends
     */
    sayAndHide(text) {
        if (!text) return;
        // Show bubble Visuals (Hide audio here)
        this.speak(text, 'neutral', 0, null, true);

        // Play Audio with callback
        if (window.AudioService) {
            window.AudioService.speak(text, window.currentLanguage, {
                onEnd: () => this.hideBubble(),
                onError: () => setTimeout(() => this.hideBubble(), 3000)
            });
        } else {
            const estimatedTime = (text.length * 70) + 2000;
            this.speechTimeout = setTimeout(() => this.hideBubble(), estimatedTime);
        }
    },

    /**
     * Hide the speech bubble
     */
    hideBubble() {
        const bubble = this.avatarElement ? this.avatarElement.querySelector('.mentor-speech-bubble') : null;
        if (bubble) bubble.classList.remove('visible');
        this.setState('neutral');

        if (window.stopAudio) {
            window.stopAudio();
        } else {
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            if (window.ElevenLabsTTS) window.ElevenLabsTTS.stop();
        }

        if (this.onBubbleClose && typeof this.onBubbleClose === 'function') {
            this.onBubbleClose();
            this.onBubbleClose = null;
        }
    }
};

// Expose under Detectimaticas namespace
if (typeof window !== 'undefined') {
    window.Detectimaticas = window.Detectimaticas || {};
    window.Detectimaticas.DetectiveMentor = DetectiveMentor;
    window.DetectiveMentor = DetectiveMentor;
}

// Auto-initialize
// Auto-init removed. Managed by Login Screen.
// if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', () => window.DetectiveMentor.init());
// } else {
//     window.DetectiveMentor.init();
// }

console.log('🕵️ detectiveMentor.js cargado');
